<template>
  <div class="add-to-calendar-button">
    <v-menu>
      <template v-slot:activator="{ props: menuProps }">
        <v-btn
          v-bind="menuProps"
          color="primary"
          variant="outlined"
          prepend-icon="fal fa-calendar-plus"
        >
          Add to Calendar
        </v-btn>
      </template>
      <v-list>
        <v-list-item @click="handleGoogleCalendar">
          <template v-slot:prepend>
            <v-icon icon="fab fa-google" />
          </template>
          <v-list-item-title>Google Calendar</v-list-item-title>
          <v-list-item-subtitle class="multiline" v-if="purchase.eventSeriesId"
            >Note: Google calendar only <br />supports first day of event
            series</v-list-item-subtitle
          >
        </v-list-item>
        <v-list-item @click="handleAppleCalendar">
          <template v-slot:prepend>
            <v-icon icon="fab fa-apple" />
          </template>
          <v-list-item-title>Apple Calendar</v-list-item-title>
        </v-list-item>
        <v-list-item @click="handleOutlookCalendar">
          <template v-slot:prepend>
            <v-icon icon="fab fa-microsoft" />
          </template>
          <v-list-item-title>Microsoft Outlook</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- Error alert -->
    <v-alert
      v-if="error"
      type="error"
      class="mt-2"
      closable
      @click:close="error = null"
    >
      {{ error }}
    </v-alert>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import useAddToCalendar from "@common/composables/useAddToCalendar";
import useStaticData from "@common/composables/useStaticData";
import { TicketPurchaseDto } from "@/models.g";

// Props
const props = defineProps<{
  purchase: TicketPurchaseDto;
  confirmationCode: string;
  publicId: string;
}>();

// Composables
const {
  generateICS,
  generateGoogleCalendarUrl,
  downloadICS,
  generateFilename,
} = useAddToCalendar();
const staticData = useStaticData();

// State
const error = ref<string | null>(null);

// Handlers
async function handleGoogleCalendar() {
  try {
    error.value = null;

    const url = generateGoogleCalendarUrl({
      purchase: props.purchase,
      confirmationCode: props.confirmationCode,
      publicId: props.publicId,
    });

    // Open Google Calendar in new tab
    window.open(url, "_blank");
  } catch (err) {
    console.error("Error generating Google Calendar link:", err);
    error.value =
      err instanceof Error
        ? err.message
        : "Unable to generate Google Calendar link. Please try again.";
  }
}

async function handleAppleCalendar() {
  try {
    error.value = null;

    const icsContent = await generateICS({
      purchase: props.purchase,
      confirmationCode: props.confirmationCode,
      publicId: props.publicId,
      supportEmail: staticData.APP_SUPPORT_EMAIL,
    });

    const filename = generateFilename(
      props.purchase.eventDate?.eventName || "Event",
      props.confirmationCode,
    );

    downloadICS(filename, icsContent);
  } catch (err) {
    console.error("Error generating Apple Calendar file:", err);
    error.value =
      err instanceof Error
        ? err.message
        : "Unable to generate calendar file. Please try again.";
  }
}

async function handleOutlookCalendar() {
  try {
    error.value = null;

    const icsContent = await generateICS({
      purchase: props.purchase,
      confirmationCode: props.confirmationCode,
      publicId: props.publicId,
      supportEmail: staticData.APP_SUPPORT_EMAIL,
    });

    const filename = generateFilename(
      props.purchase.eventDate?.eventName || "Event",
      props.confirmationCode,
    );

    downloadICS(filename, icsContent);
  } catch (err) {
    console.error("Error generating Outlook calendar file:", err);
    error.value =
      err instanceof Error
        ? err.message
        : "Unable to generate calendar file. Please try again.";
  }
}
</script>

<style scoped>
.add-to-calendar-button {
  display: inline-block;
}

.multiline {
  white-space: normal; /* allow wrapping */
  -webkit-line-clamp: unset; /* remove line clamp */
}
</style>
